import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Check, Crown, Users, Zap, Shield, Loader2, Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API } from '@/api';
import { useAuth } from '@/context/AuthContext';

const SubscriptionScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, verifyAndRefreshAuth } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [message, setMessage] = useState(null);

  const fetchSubscriptionInfo = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/subscription/info`);
      setSubscriptionInfo(response.data);
    } catch (error) {
      // Silently handle error
    }
  }, []);

  useEffect(() => {
    fetchSubscriptionInfo();
  }, [fetchSubscriptionInfo]);

  // Check for returning from Stripe checkout
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const status = searchParams.get('status');

    if (status === 'cancelled') {
      setMessage({ type: 'info', text: 'Pagamento annullato. Puoi riprovare quando vuoi.' });
      return;
    }

    if (sessionId && status === 'success') {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const checkPaymentStatus = async (sessionId) => {
    setCheckingStatus(true);
    try {
      const response = await axios.get(`${API}/subscription/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setMessage({ type: 'success', text: 'Abbonamento Pro attivato con successo!' });
        // Refresh user data
        await verifyAndRefreshAuth();
        await fetchSubscriptionInfo();
      } else {
        setMessage({ type: 'info', text: response.data.message || 'Pagamento in elaborazione...' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Errore nel verificare il pagamento. Riprova tra qualche minuto.' });
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleUpgrade = async () => {
    setLoading(true);
    setMessage(null);
    
    try {
      const response = await axios.post(`${API}/subscription/checkout`, {
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Errore nella creazione del checkout';
      setMessage({ type: 'error', text: errorMsg });
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    setMessage(null);
    
    try {
      const response = await axios.post(`${API}/subscription/portal`, {
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe Customer Portal
      window.location.href = response.data.portal_url;
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Errore nell\'apertura del portale';
      setMessage({ type: 'error', text: errorMsg });
      setPortalLoading(false);
    }
  };

  const isPro = subscriptionInfo?.status === 'pro' || user?.subscription_status === 'pro';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-4 pt-12 pb-8">
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-white/20 rounded-full transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-2xl font-bold">{t('subscription.title')}</h1>
        </div>
        <p className="text-white/80">{t('subscription.managePlan')}</p>
      </div>

      <div className="px-4 -mt-4 pb-24">
        {/* Message Banner */}
        {message && (
          <div 
            className={`mb-4 p-4 rounded-xl ${
              message.type === 'success' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
              message.type === 'error' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' :
              'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
            }`}
            data-testid="message-banner"
          >
            {message.text}
          </div>
        )}

        {/* Checking Status Loading */}
        {checkingStatus && (
          <div className="mb-4 p-4 bg-white dark:bg-gray-800 rounded-xl shadow-sm flex items-center gap-3">
            <Loader2 className="animate-spin text-indigo-600" size={24} />
            <span className="text-gray-700 dark:text-gray-300">{t('subscription.verifyingPayment')}</span>
          </div>
        )}

        {/* Current Plan Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('subscription.currentPlan')}</p>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                {isPro ? (
                  <>
                    <Crown className="text-yellow-500" size={24} />
                    {t('subscription.foreverGymPro')}
                  </>
                ) : (
                  t('subscription.free')
                )}
              </h2>
            </div>
            {isPro && (
              <span className="px-3 py-1 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-sm font-semibold rounded-full">
                {t('subscription.active')}
              </span>
            )}
          </div>
          
          {subscriptionInfo && (
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
              <Users size={18} />
              <span>
                {subscriptionInfo.client_count} 
                {subscriptionInfo.client_limit ? ` / ${subscriptionInfo.client_limit}` : ''} {t('common.clients')}
                {!subscriptionInfo.client_limit && ` (${t('common.unlimited')})`}
              </span>
            </div>
          )}

          {/* Manage Subscription Button - Only for Pro users */}
          {isPro && (
            <button
              onClick={handleManageSubscription}
              disabled={portalLoading}
              className="mt-4 w-full py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center justify-center gap-2"
              data-testid="manage-subscription-button"
            >
              {portalLoading ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  {t('common.loading')}
                </>
              ) : (
                <>
                  <Settings size={18} />
                  {t('subscription.manageSubscription')}
                </>
              )}
            </button>
          )}
        </div>

        {/* Upgrade Card - Only show if not Pro */}
        {!isPro && (
          <div className="bg-gradient-to-br from-indigo-600 to-purple-600 rounded-2xl shadow-lg p-6 text-white mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Crown size={28} />
              <h2 className="text-2xl font-bold">{t('subscription.foreverGymPro')}</h2>
            </div>
            
            <div className="mb-6">
              <span className="text-4xl font-bold">€4,99</span>
              <span className="text-white/80">/{t('subscription.priceMonth', { price: '' }).replace('/', '')}</span>
            </div>

            <ul className="space-y-3 mb-6">
              <li className="flex items-center gap-3">
                <div className="bg-white/20 rounded-full p-1">
                  <Check size={16} />
                </div>
                <span>{t('subscription.unlimitedClients')}</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="bg-white/20 rounded-full p-1">
                  <Check size={16} />
                </div>
                <span>{t('subscription.advancedStats')}</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="bg-white/20 rounded-full p-1">
                  <Check size={16} />
                </div>
                <span>{t('subscription.pdfExcelReports')}</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="bg-white/20 rounded-full p-1">
                  <Check size={16} />
                </div>
                <span>{t('subscription.prioritySupport')}</span>
              </li>
            </ul>

            <button
              onClick={handleUpgrade}
              disabled={loading}
              className="w-full py-4 bg-white text-indigo-600 font-bold rounded-xl hover:bg-gray-100 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="upgrade-button"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  {t('common.loading')}
                </>
              ) : (
                <>
                  <Zap size={20} />
                  {t('subscription.upgradeToPro')}
                </>
              )}
            </button>
          </div>
        )}

        {/* Features Comparison */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-100 dark:border-gray-700">
            <h3 className="font-semibold text-gray-900 dark:text-white">{t('subscription.comparePlans')}</h3>
          </div>
          
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            <FeatureRow feature={t('subscription.clientManagement')} free={t('subscription.upTo5')} pro={t('common.unlimited')} />
            <FeatureRow feature={t('subscription.lessonCalendar')} free="✓" pro="✓" />
            <FeatureRow feature={t('subscription.customPackages')} free="✓" pro="✓" />
            <FeatureRow feature={t('subscription.pushNotifications')} free="✓" pro="✓" />
            <FeatureRow feature={t('subscription.monthlyReports')} free="✓" pro="✓" />
            <FeatureRow feature={t('subscription.support')} free={t('subscription.email')} pro={t('subscription.priority')} />
          </div>
        </div>

        {/* Security Note */}
        <div className="mt-6 flex items-start gap-3 text-sm text-gray-500 dark:text-gray-400 pb-20">
          <Shield size={20} className="flex-shrink-0 mt-0.5" />
          <p>{t('subscription.securityNote')}</p>
        </div>
      </div>
    </div>
  );
};

const FeatureRow = ({ feature, free, pro }) => (
  <div className="flex items-center p-4">
    <span className="flex-1 text-gray-700 dark:text-gray-300">{feature}</span>
    <span className="w-24 text-center text-gray-500 dark:text-gray-400 text-sm">{free}</span>
    <span className="w-24 text-center text-indigo-600 dark:text-indigo-400 font-semibold text-sm">{pro}</span>
  </div>
);

export default SubscriptionScreen;
